import { createSlice, nanoid } from '@reduxjs/toolkit';

/**
 * @typedef {Object} Toast
 * @property {string} id - Unique identifier for the toast
 * @property {string} type - Type of toast: 'success', 'error', 'warning', 'info'
 * @property {string} message - Message content of the toast
 * @property {number} duration - Duration in milliseconds before auto-dismissal
 * @property {number} createdAt - Timestamp when the toast was created
 */

const initialState = {
  /** @type {Toast[]} */
  toasts: [],
};

const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    addToast: {
      reducer: (state, action) => {
        state.toasts.push(action.payload);
      },
      prepare: ({ type = 'info', message, duration = 3000 }) => {
        return {
          payload: {
            id: nanoid(),
            type,
            message,
            duration,
            createdAt: Date.now(),
          },
        };
      },
    },
    removeToast: (state, action) => {
      const index = state.toasts.findIndex(toast => toast.id === action.payload);
      if (index !== -1) {
        state.toasts.splice(index, 1);
      }
    },
    clearAllToasts: (state) => {
      state.toasts = [];
    },
  },
});

export const { addToast, removeToast, clearAllToasts } = toastSlice.actions;

// Selectors
export const selectToasts = state => state.toast.toasts;

export default toastSlice.reducer;

// Utility function to dispatch toast (auto-dismissal handled by middleware)
export const showToast = ({ type, message, duration }) => (dispatch) => {
  const toast = dispatch(addToast({ type, message, duration }));
  return toast.payload.id;
};

// Specific toast action creators for convenience
export const showSuccessToast = (message, duration = 3000) => showToast({ type: 'success', message, duration });
export const showErrorToast = (message, duration = 5000) => showToast({ type: 'error', message, duration });
export const showWarningToast = (message, duration = 4000) => showToast({ type: 'warning', message, duration });
export const showInfoToast = (message, duration = 3000) => showToast({ type: 'info', message, duration });
